import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu';
import { IconMove } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import { DispatchMoveWorkspaceItemsAction } from '~teacher/components/workspaces/MoveWorkspaceItemsDialog/useMoveWorkspaceItemsDialog';
import {
  isOverFolderLimit,
  isOverQuizLimit,
} from '~teacher/components/workspaces/utilities/checkWorkspaceItemLimit';
import { groupWorkspaceItemsByType } from '~teacher/components/workspaces/utilities/groupWorkspaceItemsByType';
import { MAX_WORKSPACE_ITEMS } from '~teacher/components/workspaces/WorkspaceQuizzes/actions/constants';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';

interface UseMoveFolderActionParams {
  workspaceId: string;
  dispatchMoveWorkspaceItemsDialog: DispatchMoveWorkspaceItemsAction;
  paywallCallback: () => void;
}

export function useMoveWorkspaceItemAction({
  dispatchMoveWorkspaceItemsDialog,
  workspaceId,
  paywallCallback,
}: UseMoveFolderActionParams): Action<WorkspaceItem | null> {
  const {
    permissions: {
      moveWorkspaceFolder: canMoveWorkspaceFolder,
      moveQuiz: canMoveQuiz,
    },
    roleQuery,
  } = useWorkspacePermissionsV2({ workspaceId });

  return React.useMemo(
    () => ({
      key: 'move-to',
      title: translate('Move to…'),
      icon: <IconMove />,
      disabled: ({ data }) => {
        const { folder, quiz } = groupWorkspaceItemsByType(data);
        return (
          (folder.length >= 1 && !canMoveWorkspaceFolder) ||
          (quiz.length >= 1 && !canMoveQuiz)
        );
      },
      action: ({ data, close }) => {
        const { folder, quiz } = groupWorkspaceItemsByType(data);

        if (isOverFolderLimit(folder) || isOverQuizLimit(quiz)) {
          toast.critical(
            translate('Please select less than {0} items').format(
              MAX_WORKSPACE_ITEMS
            )
          );
          close();
          return;
        }

        dispatchMoveWorkspaceItemsDialog({
          type: 'open',
          payload: {
            workspaceId: workspaceId,
            workspaceItems: data,
          },
        });
      },
      constraint: () => {
        if (roleQuery.data === WorkspaceRole.Viewer) return false;

        return true;
      },
      showProBadge: !canMoveQuiz && !canMoveWorkspaceFolder,
      paywallCallback:
        !canMoveQuiz || !canMoveWorkspaceFolder ? paywallCallback : null,
    }),
    [
      canMoveQuiz,
      canMoveWorkspaceFolder,
      paywallCallback,
      dispatchMoveWorkspaceItemsDialog,
      workspaceId,
      roleQuery.data,
    ]
  );
}
